import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useMemo, useState } from 'react';
import KpiChartPanel from '../kpi-dashboard/charts/KpiChartPanel';
import BandwidthPanelExportShell from './BandwidthPanelExportShell';
import type { BandwidthActivityAllocationMetrics, BandwidthAllocationMetrics } from './bandwidthDashboardTypes';
import type { AllocationSortDir, AllocationSortKey } from './bandwidthAllocation';
import {
  bwKpiPanelSubtitleClass,
  bwKpiTableBodyFirstColClassWithBg,
  bwKpiTableFooterRowClass,
  bwKpiTableHeaderCellClass,
  bwKpiTableHeaderClass,
  bwKpiTableHeaderFirstColClass,
  bwKpiTableSelectedRowClass,
  bwKpiTableZebraEven,
  bwKpiTableZebraOdd,
} from './bandwidthKpiTableChrome';
import { fmtBwDecimal, fmtBwPercent } from './bandwidthFormat';
import type { BandwidthExcelExport } from './bandwidthDashboardExcelExport';
import type { BandwidthPdfExport } from './bandwidthDashboardPdfExport';

export interface BandwidthAllocationTableColumn {
  key: AllocationSortKey;
  label: string;
  groupLabel?: string;
  align?: 'left' | 'right';
  kind?: 'hours' | 'pct';
}

function sortIndicator(active: boolean, dir: AllocationSortDir): string {
  if (!active) return '↕';
  return dir === 'desc' ? '↓' : '↑';
}

function ariaSortForColumn(active: boolean, dir: AllocationSortDir): 'ascending' | 'descending' | 'none' {
  if (!active) return 'none';
  return dir === 'asc' ? 'ascending' : 'descending';
}

export type AllocationTableRowKind = 'team' | 'resource' | 'activity';

export interface AllocationTableRow {
  id: string;
  label: string;
  depth?: number;
  expandable?: boolean;
  rowKind?: AllocationTableRowKind;
  teamId?: string;
  userId?: string;
  activityType?: string;
  selected?: boolean;
  metrics: BandwidthAllocationMetrics | BandwidthActivityAllocationMetrics;
}

export interface BandwidthAllocationDataTableProps {
  labelColumnTitle: string;
  rows: AllocationTableRow[];
  footer?: BandwidthAllocationMetrics | BandwidthActivityAllocationMetrics;
  columns?: BandwidthAllocationTableColumn[];
  loading?: boolean;
  emptyMessage?: string;
  errorMessage?: string | null;
  expanded?: Record<string, boolean>;
  onToggleExpand?: (id: string) => void;
  allExpanded?: boolean;
  onToggleExpandAll?: () => void;
  expandAllDisabled?: boolean;
  horizontalScroll?: boolean;
  sortKey: AllocationSortKey;
  sortDir: AllocationSortDir;
  onToggleSort: (key: AllocationSortKey) => void;
  sortHeadersDisabled?: boolean;
  onTeamClick?: (teamId: string) => void;
  onResourceClick?: (userId: string) => void;
  onActivityClick?: (activityType: string) => void;
}

export const BANDWIDTH_ALLOCATION_COLUMNS: BandwidthAllocationTableColumn[] = [
  { key: 'totalHours', label: 'Total hours', align: 'right', kind: 'hours' },
  { key: 'billableHours', label: 'Billable Hours', groupLabel: 'Against Customers', align: 'right', kind: 'hours' },
  { key: 'nonBillableHours', label: 'Non Billable Hours', groupLabel: 'Against Silicus MS', align: 'right', kind: 'hours' },
  { key: 'availableHours', label: 'Available Hours', align: 'right', kind: 'hours' },
  { key: 'bandwidthUtilizedPct', label: 'Bandwidth Utilized', align: 'right', kind: 'pct' },
  { key: 'bandwidthAvailablePct', label: 'Bandwidth Available', align: 'right', kind: 'pct' },
];

export const BANDWIDTH_ACTIVITY_ALLOCATION_COLUMNS: BandwidthAllocationTableColumn[] = [
  { key: 'totalHours', label: 'Total hours', align: 'right', kind: 'hours' },
  { key: 'billableHours', label: 'Billable Hours', groupLabel: 'Against Customers', align: 'right', kind: 'hours' },
  { key: 'nonBillableHours', label: 'Non Billable Hours', groupLabel: 'Against Silicus MS', align: 'right', kind: 'hours' },
  { key: 'billablePct', label: 'Billable %', align: 'right', kind: 'pct' },
  { key: 'nonBillablePct', label: 'Non Billable %', align: 'right', kind: 'pct' },
];

function renderMetricCell(
  key: AllocationSortKey,
  metrics: BandwidthAllocationMetrics | BandwidthActivityAllocationMetrics
): React.ReactNode {
  if (
    key === 'bandwidthUtilizedPct' ||
    key === 'bandwidthAvailablePct' ||
    key === 'billablePct' ||
    key === 'nonBillablePct'
  ) {
    const pct = metrics[key as keyof typeof metrics] as number | null | undefined;
    return fmtBwPercent(pct ?? null);
  }
  const value = metrics[key as keyof typeof metrics];
  return typeof value === 'number' ? fmtBwDecimal(value) : '—';
}

function SortableHeaderButton({
  label,
  sortKey,
  activeSortKey,
  sortDir,
  disabled,
  onToggleSort,
  align = 'right',
}: {
  label: string;
  sortKey: AllocationSortKey;
  activeSortKey: AllocationSortKey;
  sortDir: AllocationSortDir;
  disabled: boolean;
  onToggleSort: (key: AllocationSortKey) => void;
  align?: 'left' | 'right';
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onToggleSort(sortKey)}
      className={`inline-flex w-full items-center gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60 ${
        align === 'right' ? 'justify-end' : 'justify-start text-left'
      }`}
    >
      {label}
      <span className="font-normal opacity-90" aria-hidden>
        {sortIndicator(activeSortKey === sortKey, sortDir)}
      </span>
    </button>
  );
}

export const BandwidthAllocationDataTable: React.FC<BandwidthAllocationDataTableProps> = ({
  labelColumnTitle,
  rows,
  footer,
  columns = BANDWIDTH_ALLOCATION_COLUMNS,
  loading = false,
  emptyMessage = 'No approved time entries for this filter.',
  errorMessage = null,
  expanded,
  onToggleExpand,
  allExpanded = false,
  onToggleExpandAll,
  expandAllDisabled = false,
  horizontalScroll = false,
  sortKey,
  sortDir,
  onToggleSort,
  sortHeadersDisabled = false,
  onTeamClick,
  onResourceClick,
  onActivityClick,
}) => {
  const colSpan = columns.length + 1;
  const labelColMinWidth = horizontalScroll ? 'min-w-[220px]' : 'min-w-[160px]';
  const headersDisabled = sortHeadersDisabled || loading || Boolean(errorMessage) || rows.length === 0;

  const handleRowActivate = useCallback(
    (row: AllocationTableRow) => {
      if (row.rowKind === 'team' && row.teamId && onTeamClick) {
        onTeamClick(row.teamId);
        return;
      }
      if (row.rowKind === 'resource' && row.userId && onResourceClick) {
        onResourceClick(row.userId);
        return;
      }
      if (row.rowKind === 'activity' && row.activityType && onActivityClick) {
        onActivityClick(row.activityType);
      }
    },
    [onTeamClick, onResourceClick, onActivityClick]
  );

  const rowClickable = Boolean(onTeamClick || onResourceClick || onActivityClick);

  return (
    <div
      className={`min-h-0 flex-1 ${horizontalScroll ? 'overflow-x-auto overflow-y-auto' : 'overflow-auto'}`}
    >
      <table
        className={`border-collapse text-left text-xs ${
          horizontalScroll ? 'w-max min-w-[920px]' : 'w-full min-w-[760px]'
        }`}
      >
        <thead>
          <tr className={bwKpiTableHeaderClass}>
            <th
              scope="col"
              aria-sort={ariaSortForColumn(sortKey === 'label', sortDir)}
              className={`${labelColMinWidth} px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass} ${bwKpiTableHeaderFirstColClass}`}
            >
              <span className="inline-flex items-center gap-1">
                {onToggleExpandAll ? (
                  <button
                    type="button"
                    disabled={expandAllDisabled}
                    onClick={onToggleExpandAll}
                    aria-expanded={allExpanded}
                    aria-label={allExpanded ? 'Collapse all teams' : 'Expand all teams'}
                    className="-ml-1 inline-flex shrink-0 items-center rounded p-0.5 text-white hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {allExpanded ? (
                      <ChevronDownIcon className="h-4 w-4" aria-hidden />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" aria-hidden />
                    )}
                  </button>
                ) : null}
                <SortableHeaderButton
                  label={labelColumnTitle}
                  sortKey="label"
                  activeSortKey={sortKey}
                  sortDir={sortDir}
                  disabled={headersDisabled}
                  onToggleSort={onToggleSort}
                  align="left"
                />
              </span>
            </th>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === col.key, sortDir)}
                className={`min-w-[88px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                {col.groupLabel ? (
                  <div className="flex flex-col items-end">
                    <SortableHeaderButton
                      label={col.label}
                      sortKey={col.key}
                      activeSortKey={sortKey}
                      sortDir={sortDir}
                      disabled={headersDisabled}
                      onToggleSort={onToggleSort}
                    />
                    <div className="mt-0.5 text-[9px] font-normal normal-case tracking-normal text-white/85">
                      {col.groupLabel}
                    </div>
                  </div>
                ) : (
                  <SortableHeaderButton
                    label={col.label}
                    sortKey={col.key}
                    activeSortKey={sortKey}
                    sortDir={sortDir}
                    disabled={headersDisabled}
                    onToggleSort={onToggleSort}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-slate-800">
          {errorMessage ? (
            <tr>
              <td colSpan={colSpan} className="px-3 py-10 text-center text-sm text-red-700">
                {errorMessage}
              </td>
            </tr>
          ) : loading ? (
            <tr>
              <td colSpan={colSpan} className="px-3 py-10 text-center text-slate-500">
                Loading…
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="px-3 py-10 text-center text-slate-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            <>
              {rows.map((row, index) => {
                const depth = row.depth ?? 0;
                const stripe = index % 2 === 0 ? bwKpiTableZebraOdd : bwKpiTableZebraEven;
                const paddingLeft = depth > 0 ? 'pl-9' : 'px-2';
                const isOpen = expanded?.[row.id] === true;
                const rowBorder = depth > 0 ? 'border-b border-slate-100' : 'border-b border-slate-200/90';
                const selected = row.selected === true;
                const rowClass = selected
                  ? `${bwKpiTableSelectedRowClass} cursor-pointer`
                  : `${stripe} ${rowClickable ? 'cursor-pointer hover:bg-indigo-50/70' : ''}`;
                return (
                  <tr
                    key={row.id}
                    className={`${rowBorder} ${rowClass}`}
                    onClick={rowClickable ? () => handleRowActivate(row) : undefined}
                    onKeyDown={
                      rowClickable
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleRowActivate(row);
                            }
                          }
                        : undefined
                    }
                    tabIndex={rowClickable ? 0 : undefined}
                    role={rowClickable ? 'button' : undefined}
                  >
                    <td
                      className={`${labelColMinWidth} ${paddingLeft} py-2 font-medium text-slate-900 ${bwKpiTableBodyFirstColClassWithBg({ rowIndex: index, selected })}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {row.expandable && onToggleExpand ? (
                          <button
                            type="button"
                            className="rounded p-0.5 text-slate-600 hover:bg-slate-200/80"
                            aria-expanded={isOpen}
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleExpand(row.id);
                            }}
                          >
                            {isOpen ? (
                              <ChevronDownIcon className="h-4 w-4" aria-hidden />
                            ) : (
                              <ChevronRightIcon className="h-4 w-4" aria-hidden />
                            )}
                          </button>
                        ) : depth > 0 ? (
                          <span className="inline-block w-5 shrink-0" aria-hidden />
                        ) : null}
                        <span className={depth === 0 ? 'font-semibold leading-snug' : ''}>{row.label}</span>
                      </span>
                    </td>
                    {columns.map((col) => (
                      <td key={col.key} className="px-3 py-2 text-right tabular-nums">
                        {renderMetricCell(col.key, row.metrics)}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {footer ? (
                <tr className={bwKpiTableFooterRowClass}>
                  <td className={`px-3 py-2.5 ${bwKpiTableBodyFirstColClassWithBg({ footer: true })}`}>Total</td>
                  {columns.map((col) => (
                    <td key={col.key} className="px-3 py-2.5 text-right tabular-nums">
                      {renderMetricCell(col.key, footer)}
                    </td>
                  ))}
                </tr>
              ) : null}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

export interface BandwidthAllocationPanelProps {
  title: string;
  subtitle?: string;
  labelColumnTitle: string;
  rows: AllocationTableRow[];
  footer?: BandwidthAllocationMetrics | BandwidthActivityAllocationMetrics;
  columns?: BandwidthAllocationTableColumn[];
  loading?: boolean;
  emptyMessage?: string;
  errorMessage?: string | null;
  expanded?: Record<string, boolean>;
  onToggleExpand?: (id: string) => void;
  allExpanded?: boolean;
  onToggleExpandAll?: () => void;
  expandAllDisabled?: boolean;
  horizontalScroll?: boolean;
  sortKey: AllocationSortKey;
  sortDir: AllocationSortDir;
  onToggleSort: (key: AllocationSortKey) => void;
  buildExcel: () => BandwidthExcelExport | null;
  buildPdf: () => BandwidthPdfExport | null;
  exportAriaLabel: string;
  onTeamClick?: (teamId: string) => void;
  onResourceClick?: (userId: string) => void;
  onActivityClick?: (activityType: string) => void;
}

export const BandwidthAllocationPanel: React.FC<BandwidthAllocationPanelProps> = ({
  title,
  subtitle,
  horizontalScroll = false,
  loading = false,
  errorMessage = null,
  rows,
  columns,
  buildExcel,
  buildPdf,
  exportAriaLabel,
  ...tableProps
}) => {
  const exportDisabled = loading || Boolean(errorMessage) || rows.length === 0;

  return (
    <KpiChartPanel
      title={title}
      titleClassName="!pl-0"
      titleEnd={
        <BandwidthPanelExportShell
          disabled={exportDisabled}
          ariaLabel={exportAriaLabel}
          buildPdf={buildPdf}
          buildExcel={buildExcel}
        />
      }
      className={`h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[440px] ${horizontalScroll ? 'min-w-0' : ''}`}
      flexFill
      bodyClassName={`px-0 pb-0 pt-0 ${horizontalScroll ? 'min-w-0' : ''}`}
    >
      {subtitle ? <p className={bwKpiPanelSubtitleClass}>{subtitle}</p> : null}
      <BandwidthAllocationDataTable
        {...tableProps}
        rows={rows}
        columns={columns}
        loading={loading}
        errorMessage={errorMessage}
        horizontalScroll={horizontalScroll}
      />
    </KpiChartPanel>
  );
};

export function useExpandableRows(teamIds: string[], autoExpandIds: string[] = []) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const teamKey = useMemo(() => teamIds.slice().sort().join('|'), [teamIds]);
  const autoExpandKey = useMemo(() => autoExpandIds.slice().sort().join('|'), [autoExpandIds]);

  React.useEffect(() => {
    setExpanded((prev) => {
      const next: Record<string, boolean> = { ...prev };
      for (const id of teamIds) {
        if (next[id] === undefined) next[id] = false;
      }
      return next;
    });
  }, [teamKey, teamIds]);

  React.useEffect(() => {
    if (!autoExpandIds.length) return;
    setExpanded((prev) => {
      const next = { ...prev };
      for (const id of autoExpandIds) {
        next[id] = true;
      }
      return next;
    });
  }, [autoExpandKey, autoExpandIds]);

  const toggle = useCallback((id: string) => {
    setExpanded((p) => ({ ...p, [id]: !p[id] }));
  }, []);

  const expandableIds = useMemo(() => teamIds, [teamIds]);

  const allExpanded = useMemo(() => {
    if (expandableIds.length === 0) return false;
    return expandableIds.every((id) => expanded[id] === true);
  }, [expandableIds, expanded]);

  const toggleExpandAll = useCallback(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      const target = !expandableIds.every((id) => prev[id] === true);
      for (const id of expandableIds) {
        next[id] = target;
      }
      return next;
    });
  }, [expandableIds]);

  return { expanded, toggle, allExpanded, toggleExpandAll };
}

export function useAllocationSort(defaultKey: AllocationSortKey = 'totalHours') {
  const [sortKey, setSortKey] = useState<AllocationSortKey>(defaultKey);
  const [sortDir, setSortDir] = useState<AllocationSortDir>('desc');

  const toggleSort = useCallback(
    (key: AllocationSortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortKey(key);
        setSortDir(key === 'label' ? 'asc' : 'desc');
      }
    },
    [sortKey]
  );

  return { sortKey, sortDir, toggleSort };
}
